import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ImageUploadField } from '../components/ImageUploadField';
import { HeaderGifBuilder } from '../components/HeaderGifBuilder';
import { headerService } from '../services/headerService';
import { fieldClass, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type HeaderRow = {
  id: string;
  title: string;
  subtitle: string;
  badge: string;
  image: string;
  targetUrl: string;
  ctaText: string;
  order: number;
  isActive: boolean;
};

type HeaderFormState = {
  title: string;
  subtitle: string;
  image: string;
  targetUrl: string;
  order: number;
  isActive: boolean;
};

const emptyForm = (): HeaderFormState => ({
  title: '',
  subtitle: '',
  image: '',
  targetUrl: '/products',
  order: 0,
  isActive: true,
});

function num(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function mapRow(raw: Record<string, unknown>): HeaderRow {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    subtitle: String(raw.subtitle ?? raw.description ?? ''),
    badge: String(raw.badge ?? ''),
    image: String(raw.image ?? ''),
    targetUrl: String(raw.targetUrl ?? raw.ctaLink ?? '/products'),
    ctaText: String(raw.ctaText ?? 'مشاهده'),
    order: num(raw.order),
    isActive: Boolean(raw.isActive),
  };
}

function formFromRow(row: HeaderRow): HeaderFormState {
  return {
    title: row.title,
    subtitle: row.subtitle,
    image: row.image,
    targetUrl: row.targetUrl,
    order: row.order,
    isActive: row.isActive,
  };
}

function payloadFromForm(form: HeaderFormState): Record<string, unknown> {
  return {
    title: form.title.trim(),
    subtitle: form.subtitle.trim(),
    description: form.subtitle.trim(),
    badge: '',
    image: form.image.trim(),
    targetUrl: form.targetUrl.trim() || '/products',
    ctaLink: form.targetUrl.trim() || '/products',
    ctaText: '',
    order: num(form.order),
    isActive: form.isActive,
  };
}

export function HeadersPage() {
  const [items, setItems] = useState<HeaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editing, setEditing] = useState<HeaderRow | null>(null);
  const [viewing, setViewing] = useState<HeaderRow | null>(null);
  const [form, setForm] = useState<HeaderFormState>(emptyForm);

  const activeCount = items.filter((i) => i.isActive).length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await headerService.list();
      setItems(data.map(mapRow));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت هدر');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: HeaderRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('عنوان الزامی است');
      return;
    }
    if (!form.image.trim()) {
      toast.error('یک GIF بسازید یا تصویر آپلود کنید');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await headerService.update(editing.id, payload);
        toast.success('آیتم هدر به‌روزرسانی شد');
      } else {
        await headerService.create(payload);
        toast.success('آیتم هدر ایجاد شد');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: HeaderRow) => {
    if (!window.confirm(`آیتم «${row.title}» حذف شود؟`)) return;
    try {
      await headerService.remove(row.id);
      toast.success('حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const setField = <K extends keyof HeaderFormState>(key: K, value: HeaderFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">هدر سایت</h1>
          <p className="text-slate-600 mt-1">نوار اعلان بالای فروشگاه — متن وسط‌چین، بدون دکمه لینک</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن آیتم
        </button>
      </div>

      <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
        <div className="flex gap-2">
          <Info className="h-5 w-5 flex-none text-sky-600 mt-0.5" />
          <div className="space-y-1.5 leading-7">
            <p className="font-semibold">نحوه نمایش برای مشتری</p>
            <ul className="list-disc pr-5 space-y-1 text-sky-900/90">
              <li>محتوا وسط صفحه دیده می‌شود؛ لینک روی خود هدر است و دکمه‌ای نشان داده نمی‌شود.</li>
              <li>با سازنده GIF می‌توانید متن + گرادیان + حرکت بسازید و مستقیم روی هدر بگذارید.</li>
              <li>
                وضعیت فعلی: {activeCount === 0 ? 'خاموش' : `${activeCount} آیتم فعال`}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز آیتم هدری ثبت نشده. با افزودن یک آیتم فعال، نوار بالای سایت روشن می‌شود.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="admin-table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-right font-medium">ردیف</th>
                  <th className="px-4 py-3 text-right font-medium">آیتم</th>
                  <th className="px-4 py-3 text-right font-medium">ترتیب</th>
                  <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                  <th className="px-4 py-3 text-right font-medium">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((row, index) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {row.image ? (
                          <img src={row.image} alt="" className="h-12 w-28 rounded-lg object-cover" />
                        ) : (
                          <div className="flex h-12 w-28 items-center justify-center rounded-lg bg-slate-100 text-xs text-slate-400">
                            بدون GIF
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-slate-900">{row.title}</div>
                          <div className="text-xs text-slate-500 mt-0.5 line-clamp-1" dir="ltr">
                            {row.targetUrl}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.order}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                          row.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {row.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setViewing(row);
                            setViewOpen(true);
                          }}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                          title="مشاهده"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(row)}
                          className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                          title="ویرایش"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete(row)}
                          className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                          title="حذف"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش آیتم هدر' : 'افزودن آیتم هدر'}
        size="xl"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div>
            <label className={labelClass}>نام داخلی (فقط پنل — روی سایت و GIF اثر ندارد)</label>
            <input className={fieldClass} value={form.title} onChange={(e) => setField('title', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>یادداشت داخلی (اختیاری — روی سایت دیده نمی‌شود)</label>
            <input
              className={fieldClass}
              value={form.subtitle}
              onChange={(e) => setField('subtitle', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>لینک مقصد (مخفی از مشتری — با کلیک روی هدر باز می‌شود)</label>
            <input
              className={fieldClass}
              value={form.targetUrl}
              onChange={(e) => setField('targetUrl', e.target.value)}
              dir="ltr"
              placeholder="/products"
            />
          </div>

          <HeaderGifBuilder
            key={editing?.id ?? `new-${formOpen ? '1' : '0'}`}
            onGenerated={(url) => {
              setField('image', url);
            }}
          />

          <ImageUploadField
            label="یا آپلود GIF / تصویر آماده"
            value={form.image}
            onChange={(url) => setField('image', url)}
            hint="اگر با سازنده GIF ساختید، اینجا پر می‌شود. آپلود دستی GIF هم مجاز است."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>ترتیب</label>
              <input
                type="number"
                className={fieldClass}
                value={form.order}
                onChange={(e) => setField('order', num(e.target.value))}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setField('isActive', e.target.checked)}
              />
              فعال (در سایت نمایش داده شود)
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewing(null);
        }}
        title={viewing?.title || 'مشاهده'}
        size="md"
      >
        {viewing && (
          <div className="space-y-3" dir="rtl">
            {viewing.image ? (
              <img src={viewing.image} alt="" className="h-20 w-full rounded-xl object-cover" />
            ) : null}
            <p className="text-xs text-slate-500" dir="ltr">
              لینک مقصد: {viewing.targetUrl}
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
